import { BusinessRisk, ProposalDetails } from '@/services/quotes/types';
import { isClonedQuoteMarkedAsDraft } from '@/shared/utils/clone-quote-status.util';
import { getFormattedCurrency } from '@/shared/utils/currencyFormatter';
import { cleanCnpjOrCpf } from '@/shared/utils/document.util';
import {
  formatRegisterNumber,
  resolveRegisterNumber,
} from '@/shared/utils/format-register-number';
import { RequiredTooltipProvider } from '@/shared/components/required-field-tooltip';
import { Steps } from '@noorden-insurtech/alba-design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuoteSchemas } from '../../quotes-schemas';
import { useQuoteParams } from '../context/quote-params.context';
import { useQuoteSummary } from '../context/quote-summary.context';
import { useQuoteForm } from '../hooks/common/use-quote-form.hook';
import { useAssistances } from '../hooks/homeowner/use-assistances.hook';
import { useConstructionTypes } from '../hooks/homeowner/use-construction-types.hook';
import { useCoverages } from '../hooks/homeowner/use-coverages.hook';
import { useInsuredObjects } from '../hooks/homeowner/use-insured-objects.hook';
import { usePlanResourcesByCode } from '../hooks/homeowner/use-plan-resources-by-code.hook';
import { usePropertyClassifications } from '../hooks/homeowner/use-property-classifications.hook';
import { usePropertyTypes } from '../hooks/homeowner/use-property-types.hook';
import type { QuoteFormValues } from '../types/quote-form.types';
import { parseProductCode } from '../utils/parse-product-code';
import {
  mapDeductibleOptions,
  type CoverageCardData,
} from './coverage-card.component';
import { NewQuoteFormSkeleton } from './new-quote-form-skeleton.component';
import {
  StepCalculation,
  StepCommercialization,
  StepCoverages,
  StepInsuredData,
  StepPropertyData,
  StepRiskBusinessData,
} from './quote-steps';
import { QuoteSummary } from './quote-summary.component';

type Props = {
  proposalDetails: ProposalDetails;
  mode: 'edit' | 'view';
  // eslint-disable-next-line no-unused-vars
  onLastStepChange?: (isLastStep: boolean) => void;
};

export function EditQuoteForm({
  proposalDetails,
  mode,
  onLastStepChange,
}: Props) {
  const { productId, category, productCode, setParams } = useQuoteParams();
  const {
    updateProponent,
    updateInsuranceData,
    updateProperty,
    updateCoverages,
    updateAssistance,
    updateCommercialization,
  } = useQuoteSummary();
  const isViewMode = mode === 'view';
  // Deriva do detalhe da proposta (síncrono) em vez do contexto, que fica
  // vazio na janela de hidratação e deixaria a query disparar indevidamente.
  const isBusinessQuote =
    parseProductCode(proposalDetails.productCode || productCode || '') ===
    'BUSINESS_INSURANCE';
  const [isPrefilled, setIsPrefilled] = useState(false);

  const { data: propertyTypes } = usePropertyTypes();
  const { data: constructionTypes } = useConstructionTypes();
  const { data: propertyClassifications } = usePropertyClassifications();
  const { data: insuredObjectTypes } = useInsuredObjects();
  const versionDate = '2025-11-03';
  // Catálogo por produto usado apenas no fluxo residencial; o empresarial
  // lista as coberturas por atividade (useBusinessCoverages) nos cards de local.
  const { data: coveragesCatalog } = useCoverages(versionDate, {
    enabled: !isBusinessQuote,
  });

  const [coveragesState, setCoveragesState] = useState<CoverageCardData[]>([]);
  const [assistanceChoice, setAssistanceChoice] = useState<string>('');
  const [localInsuredObjects, setLocalInsuredObjects] = useState<
    { insuredObjectType: string; riskValue: string }[]
  >([]);
  const { data: assists } = useAssistances();
  const { data: planResources } = usePlanResourcesByCode(assistanceChoice);

  // Valor em risco (VRD) usado para validar o LMI da cobertura básica.
  // Prioriza o valor fresco editado na etapa 2 (localInsuredObjects); cai para
  // o scheduleExt do detalhe apenas antes da etapa ser visitada. Não usa o
  // prefill nem o PolicyScheduleExtList (legado) para evitar valor defasado.
  const vrdTotal = useMemo(() => {
    if (localInsuredObjects.length > 0) {
      return localInsuredObjects.reduce((acc, row) => {
        const numeric = parseInt(
          String(row.riskValue || '').replace(/\D/g, ''),
          10
        );
        if (Number.isNaN(numeric)) return acc;
        return acc + numeric / 100;
      }, 0);
    }
    const risk = proposalDetails?.lobs?.[0]?.risks?.[0];
    const scheduleExt = (risk?.scheduleExt ?? [])
      .map(item =>
        typeof item.value === 'number' && !Number.isNaN(item.value)
          ? item.value
          : 0
      )
      .filter(v => v > 0);
    return scheduleExt.reduce((acc, v) => acc + v, 0);
  }, [proposalDetails, localInsuredObjects]);

  const resolveCoverageLabel = (code?: string) => {
    const resolvedCode = (code || '').trim();
    if (!resolvedCode) return 'Cobertura';
    const match = coveragesCatalog?.find(cov => cov.code === resolvedCode);
    return match?.name?.trim() || resolvedCode;
  };

  const formatPackageDisplayName = (name: string) => {
    const n = (name || '').trim();
    if (!n) return '';
    return n.toLowerCase().startsWith('pacote ') ? n : `Pacote ${n}`;
  };

  const getPlanLabel = (planCode?: string) =>
    (assists || []).find(plan => plan.code === planCode)?.name || '';

  // O corretor principal é sempre agents[0] na resposta da API.
  // agents[0] = corretor, agents[1] = assessoria, agents[2] = executivo de vendas.
  // Co-corretores existem somente a partir de agents[3].
  const resolvedMainAgent = useMemo(() => {
    const agents = proposalDetails.agents || [];
    if (agents.length === 0) return { agent: undefined, index: -1 };
    return { agent: agents[0], index: 0 };
  }, [proposalDetails.agents]);

  // Recupera o percentual de corretagem original a partir do commissionRate e shareRate.
  // Quando o step 3 nunca foi preenchido, o backend ainda devolve o corretor
  // principal com commissionRate/shareRate = 0; tratamos esse caso como
  // "sem prefill" para que o StepCommercialization aplique o default
  // (participação 100% e corretagem vazia), igual ao fluxo de nova cotação.
  const deriveCommissionPercent = (
    agent?: typeof proposalDetails.agents extends (infer T)[] | undefined
      ? T
      : never
  ): string => {
    if (!agent || agent.commissionRate === undefined) return '';
    const shareRate = agent.shareRate || 0;
    const computed =
      shareRate > 0
        ? ((agent.commissionRate || 0) / shareRate) * 100
        : (agent.commissionRate || 0) * 100;
    return computed > 0 ? String(computed) : '';
  };

  const deriveParticipationPercent = (
    agent?: typeof proposalDetails.agents extends (infer T)[] | undefined
      ? T
      : never
  ): string => {
    if (!agent || agent.shareRate === undefined) return '';
    const computed = (agent.shareRate || 0) * 100;
    return computed > 0 ? String(computed) : '';
  };

  const resolvedCoBrokers = useMemo(() => {
    const agents = proposalDetails.agents || [];
    if (agents.length <= 3) return [];
    return agents
      .slice(3)
      .filter(
        agent =>
          Boolean((agent.name || '').trim()) &&
          agent.shareRate !== undefined &&
          agent.shareRate > 0
      )
      .map(agent => ({
        id: agent.agentCode || '',
        name: agent.name,
        shareRate: agent.shareRate,
      }));
  }, [proposalDetails.agents]);

  const { data: schema, isLoading } = useQuoteSchemas(
    category || 'HOME_INSURANCE'
  );
  const steps = schema?.steps ?? [];
  const [activeCode, setActiveCode] = useState('');

  useEffect(() => {
    if (!steps.length || activeCode) return;
    if (isViewMode) {
      setActiveCode(steps[steps.length - 1]?.code?.trim() ?? '');
    } else {
      setActiveCode(steps[0]?.code?.trim() ?? '');
    }
  }, [steps, isViewMode, activeCode]);

  const activeIndex = useMemo(
    () => steps.findIndex(step => step.code === activeCode),
    [steps, activeCode]
  );

  const isLastStep =
    steps.length > 0 && activeIndex >= 0 && activeIndex === steps.length - 1;

  useEffect(() => {
    onLastStepChange?.(isLastStep);
  }, [isLastStep, onLastStepChange]);

  const mainForm = useQuoteForm({
    defaultValues: {
      issuanceType: '1',
      startDate: '',
      endDate: '',
      isLegalPerson: false,
      name: '',
      hasSocialName: false,
      socialName: '',
      registerNumber: '',
      phone: '',
      email: '',
      policyNumber: '',
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      propertyType: '',
      constructionType: '',
      propertyClassification: '',
      insuredObjectType: '',
      activityType: '',
      riskValue: '',
      isLandmarked: false,
      congener: '',
      bonus: '-1',
    } as Partial<QuoteFormValues>,
    onSubmit: async () => {
      // TODO: Enviar para o backend
    },
  });

  const setValue = (key: string, value: unknown) =>
    // @ts-expect-error â€“ compatibilidade de chaves planas com QuoteFormData
    mainForm.setFieldValue(key, value);

  useEffect(() => {
    if (proposalDetails) {
      setParams({
        policyId: proposalDetails.policyId,
        id: proposalDetails.policyId,
        productId: String(proposalDetails.productId),
        productCode: proposalDetails.productCode,
      });
    }
  }, [proposalDetails, setParams]);

  useEffect(() => {
    if (!proposalDetails || isPrefilled) return;

    const { customer } = proposalDetails;
    const risk = proposalDetails.lobs?.[0]?.risks?.[0];
    const resolvedCep = (risk?.zipCode || customer?.zipCode || '').trim();
    const resolvedStreet = (risk?.address || customer?.address || '').trim();
    const resolvedNumber = String(
      risk?.addressNumber ?? customer?.addressNumber ?? ''
    ).trim();
    const resolvedNeighborhood = (
      risk?.neighborhood ||
      customer?.neighborhood ||
      ''
    ).trim();
    const resolvedCity = (risk?.city || customer?.city || '').trim();
    const resolvedState = (risk?.state || customer?.state || '').trim();
    const rawCpf = (customer?.cpf || '').trim();
    const rawIdNo = (customer?.idNo || '').trim();

    // Normalizador canônico (CNPJ pode ser alfanumérico — nunca /\D/g aqui).
    const cleanCpf = cleanCnpjOrCpf(rawCpf);
    const cleanIdNo = cleanCnpjOrCpf(rawIdNo);

    // Prioriza CPF válido, só usa idNo se for CNPJ (>11 caracteres) ou se CPF
    // estiver vazio — mesma regra de `resolveRegisterNumber` (shared).
    const useIdNoDocument =
      cleanIdNo.length > 11 || (cleanCpf.length === 0 && cleanIdNo.length > 0);
    const rawDocument = (useIdNoDocument ? rawIdNo : rawCpf || rawIdNo).trim();
    const cleanDocument = resolveRegisterNumber(rawCpf, rawIdNo);
    // PJ é definido pelo tipo do documento do cliente (2 = CNPJ); o tamanho
    // do documento é só fallback para registros sem `documentType`.
    const isLegalPerson = customer?.documentType
      ? customer.documentType === '2'
      : cleanDocument.length > 11;
    const formattedDocument =
      cleanDocument && formatRegisterNumber(cleanDocument)
        ? formatRegisterNumber(cleanDocument)
        : rawDocument;

    const hasSocialNameFromApi =
      !!customer?.socialName && customer.socialName !== customer.name;

    const toDateOnly = (value?: string) => {
      if (!value) return '';
      return value.split('T')[0] || value;
    };

    const p = proposalDetails as ProposalDetails & {
      EffectiveDate?: string;
      ExpiryDate?: string;
      TerminateDate?: string;
      InsertBy?: string;
      QuotationDate?: string;
    };
    const rawEffectiveDate =
      proposalDetails.effectiveDate || p.EffectiveDate || '';
    const rawExpiryDate =
      proposalDetails.expiryDate ||
      proposalDetails.terminateDate ||
      p.ExpiryDate ||
      p.TerminateDate ||
      '';

    const rawInclusionDate =
      proposalDetails.quotationDate || p.QuotationDate || '';
    const isClonedDraft = isClonedQuoteMarkedAsDraft(proposalDetails.policyId);

    const maxIsoDate = (a: string, b: string) => {
      if (!a) return b;
      if (!b) return a;
      const ta = new Date(`${a}T12:00:00`).getTime();
      const tb = new Date(`${b}T12:00:00`).getTime();
      if (Number.isNaN(ta)) return b;
      if (Number.isNaN(tb)) return a;
      return ta >= tb ? a : b;
    };

    const addOneYear = (isoDate: string) => {
      if (!isoDate) return '';
      const base = new Date(`${isoDate}T12:00:00`);
      if (Number.isNaN(base.getTime())) return '';
      base.setFullYear(base.getFullYear() + 1);
      return base.toISOString().split('T')[0];
    };

    const inclusionDateOnly = toDateOnly(rawInclusionDate);
    const effectiveDateOnly = toDateOnly(rawEffectiveDate);
    const expiryDateOnly = toDateOnly(rawExpiryDate);
    const effectiveDateForForm = isClonedDraft
      ? maxIsoDate(effectiveDateOnly, inclusionDateOnly)
      : effectiveDateOnly;
    const expiryDateForForm = isClonedDraft
      ? addOneYear(effectiveDateForForm)
      : expiryDateOnly;

    const resolveOptionCodeFromApiValue = <T extends { code?: unknown }>(
      apiValue: string | undefined,
      options: T[] | undefined
    ) => {
      const v = String(apiValue ?? '').trim();
      if (!v) return '';

      if (options?.some(opt => String(opt.code ?? '').trim() === v)) {
        return v;
      }

      const asNumber = Number(v);
      if (
        Number.isFinite(asNumber) &&
        asNumber >= 1 &&
        options &&
        options.length >= asNumber
      ) {
        const resolved = options[asNumber - 1]?.code;
        return resolved ? String(resolved) : v;
      }

      return v;
    };

    const formatRiskValue = (value?: number) => {
      if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) {
        return '';
      }
      return value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
    };

    const mapInsuredObjectCodeToLabel = (code: string) => {
      const resolvedCode = String(code || '').trim();
      if (!resolvedCode) return '';
      return (
        insuredObjectTypes?.find(
          item => String(item.code ?? '').trim() === resolvedCode
        )?.description || resolvedCode
      );
    };

    const issuanceTypeValue = String(
      proposalDetails.typeOfInsurance || proposalDetails.policyType || '1'
    ) as '1' | '3';
    // Dados de renovação (congênere, apólice, bônus) só valem para renovação
    // externa: seguro novo não deve carregar valores salvos antes da troca
    const isRenewalIssuance = issuanceTypeValue === '3';

    setValue('issuanceType', issuanceTypeValue);
    setValue('startDate', effectiveDateForForm);
    setValue('endDate', expiryDateForForm);

    setValue('isLegalPerson', isLegalPerson);
    setValue('registerNumber', formattedDocument || rawDocument);
    setValue('name', customer?.name || '');
    setValue('hasSocialName', hasSocialNameFromApi);
    setValue(
      'socialName',
      hasSocialNameFromApi ? customer?.socialName || '' : ''
    );
    setValue('email', customer?.email || '');
    setValue('phone', customer?.cellphone || customer?.phone || '');
    setValue(
      'congener',
      isRenewalIssuance ? proposalDetails.previousInsurer || '' : ''
    );
    setValue(
      'bonus',
      isRenewalIssuance
        ? proposalDetails.bonusExperience ||
            proposalDetails.experienceBonus ||
            '-1'
        : '-1'
    );
    setValue(
      'policyNumber',
      isRenewalIssuance ? proposalDetails.previousPolicy || '' : ''
    );
    setValue('claim', isRenewalIssuance ? proposalDetails.sinister || '' : '');
    setValue(
      'claimValue',
      isRenewalIssuance ? formatRiskValue(proposalDetails.sinisterValue) : ''
    );

    setValue('cep', resolvedCep);
    setValue('street', resolvedStreet);
    setValue('number', resolvedNumber);
    // Complemento nem sempre vem no risco; mantÃ©m o do customer quando existir
    setValue('complement', customer?.addressComplement || '');
    setValue('neighborhood', resolvedNeighborhood);
    setValue('city', resolvedCity);
    setValue('state', resolvedState);

    updateProperty({
      city: resolvedCity || undefined,
      state: resolvedState || undefined,
      cep: resolvedCep || undefined,
      address: resolvedStreet || undefined,
      number: resolvedNumber || undefined,
      complement: customer?.addressComplement,
      neighborhood: resolvedNeighborhood || undefined,
    });

    // Prefill dados do risco (imÃ³vel) vindos no detalhe
    if (risk) {
      const resolvedPropertyTypeCode = resolveOptionCodeFromApiValue(
        String(risk.propertyType ?? ''),
        propertyTypes
      );
      const resolvedConstructionTypeCode = resolveOptionCodeFromApiValue(
        String(risk.constructionType ?? ''),
        constructionTypes
      );
      const resolvedPropertyClassificationCode = resolveOptionCodeFromApiValue(
        String(risk.propertyClassification ?? ''),
        propertyClassifications
      );
      const scheduleExtFromApi = (risk.scheduleExt ?? [])
        .map(item => ({
          insuredObjectType: String(item.insuredObject ?? '').trim(),
          riskValue: formatRiskValue(item.value),
        }))
        .filter(item => item.insuredObjectType);

      const scheduleExtFromLegacyApi = (risk.PolicyScheduleExtList ?? [])
        .map(item => ({
          insuredObjectType: String(item.ObjetoSegurado ?? '').trim(),
          riskValue: formatRiskValue(item.RealValue),
        }))
        .filter(item => item.insuredObjectType);

      // Usa scheduleExt como fonte primÃ¡ria; cai para o legado apenas se vazio
      const prefilledInsuredObjects =
        scheduleExtFromApi.length > 0
          ? scheduleExtFromApi
          : scheduleExtFromLegacyApi;
      const primaryInsuredObjectType =
        prefilledInsuredObjects[0]?.insuredObjectType || '';
      const primaryInsuredObjectTypeLabel = mapInsuredObjectCodeToLabel(
        primaryInsuredObjectType
      );

      setValue('propertyType', resolvedPropertyTypeCode);
      setValue('constructionType', resolvedConstructionTypeCode);
      setValue('propertyClassification', resolvedPropertyClassificationCode);
      setValue('insuredObjectType', primaryInsuredObjectType);
      setValue(
        'isLandmarked',
        String(risk.listedPropertyFlag || '').toUpperCase() === 'S'
      );

      const propertyTypeName =
        propertyTypes?.find(
          pt => String(pt.code ?? '') === resolvedPropertyTypeCode
        )?.name || '';
      const propertyTypeValue =
        resolvedPropertyTypeCode === '1' ||
          propertyTypeName.toLowerCase().includes('casa')
          ? ('house' as const)
          : ('apartment' as const);

      const constructionTypeLabel = constructionTypes?.find(
        type => String(type.code ?? '') === resolvedConstructionTypeCode
      )?.description;

      const propertyClassification = resolvedPropertyClassificationCode;
      const propertyClassificationLabel = propertyClassifications?.find(
        item => String(item.code ?? '') === resolvedPropertyClassificationCode
      )?.description;

      updateProperty({
        propertyType: propertyTypeValue,
        constructionType: resolvedConstructionTypeCode,
        constructionTypeLabel,
        propertyClassification,
        propertyClassificationLabel,
        insuredObjectType: primaryInsuredObjectType,
        insuredObjectTypeLabel: primaryInsuredObjectTypeLabel,
      });
    }

    updateProponent({
      name: customer?.name || '',
      document: formattedDocument || rawDocument || '',
      personType: isLegalPerson ? 'PJ' : 'PF',
      socialName: customer?.socialName || '',
    });
    // insertByUserName = e-mail do usuÃ¡rio no backend; sÃ³ informativo no resumo.
    const inclusionUserFromPolicy =
      proposalDetails.insertByUserName?.trim() || '';

    updateInsuranceData({
      inclusionDate: rawInclusionDate,
      validityStart: toDateOnly(rawEffectiveDate),
      validityEnd: toDateOnly(rawExpiryDate),
      expirationDate: toDateOnly(rawExpiryDate),
      inclusionUser: inclusionUserFromPolicy || '-',
      insuranceType: isRenewalIssuance ? 'renewal' : 'new',
      congener: isRenewalIssuance ? proposalDetails.previousInsurer || '' : '',
      policyNumber: isRenewalIssuance
        ? proposalDetails.previousPolicy || ''
        : '',
    });

    if (risk?.coverages && risk.coverages.length > 0) {
      const existingCoverages = risk.coverages.filter(
        cov => cov.productElementCode !== 'ASSIST'
      );

      const existingByCode = new Map<
        string,
        (typeof existingCoverages)[number]
      >();
      existingCoverages.forEach(cov => {
        const code = String(cov.productElementCode || '').trim();
        if (!code) return;
        existingByCode.set(code, cov);
      });

      const mappedCoverages: CoverageCardData[] =
        Array.isArray(coveragesCatalog) && coveragesCatalog.length > 0
          ? (coveragesCatalog
            .map(item => {
              if (String(item.code || '').trim() === 'ASSIST') {
                return null;
              }
              const existing = existingByCode.get(item.code);
              const min = item.limit?.constraints?.min ?? 0;
              const max = item.limit?.constraints?.max ?? 999999999;

              const stableId = String(item.code || '').trim() || item.id;
              return {
                id: stableId,
                code: item.code,
                productElementId: item.productElementId,
                title: resolveCoverageLabel(item.code),
                description: item.description || '',
                required: item.required,
                limit: {
                  type: 'range',
                  constraints: { min, max },
                },
                limitValue:
                  existing?.sumInsured !== undefined
                    ? getFormattedCurrency(Number(existing.sumInsured))
                    : '',
                deductible: (() => {
                  if (
                    existing?.codeDeductible !== undefined &&
                    existing.codeDeductible !== 0
                  ) {
                    const matchedDeductible = (item.deductibles ?? []).find(
                      d => String(d.code) === String(existing.codeDeductible)
                    );
                    if (matchedDeductible)
                      return String(matchedDeductible.code);
                  }
                  return '';
                })(),
                deductibleOptions: mapDeductibleOptions(
                  item.deductibles ?? []
                ),
                indemnityPeriod:
                  existing?.indemnityPeriod !== undefined
                    ? String(existing.indemnityPeriod)
                    : item.required
                      ? ''
                      : undefined,
              };
            })
            .filter(Boolean) as CoverageCardData[])
          : existingCoverages.map(cov => ({
            id: cov.productElementCode || String(cov.productElementId || ''),
            code: cov.productElementCode,
            productElementId: cov.productElementId
              ? String(cov.productElementId)
              : undefined,
            title: resolveCoverageLabel(cov.productElementCode),
            description:
              coveragesCatalog?.find(c => c.code === cov.productElementCode)
                ?.description || '',
            required:
              cov.isFinalLevelCt === 'Y' &&
              cov.productElementCode !== 'ASSIST',
            limit: {
              type: 'range',
              constraints: { min: 0, max: 999999999 },
            },
            limitValue:
              cov.sumInsured !== undefined
                ? getFormattedCurrency(Number(cov.sumInsured))
                : '',
            deductible:
              cov.codeDeductible !== undefined && cov.codeDeductible !== 0
                ? String(cov.codeDeductible)
                : '',
            indemnityPeriod:
              cov.indemnityPeriod !== undefined
                ? String(cov.indemnityPeriod)
                : '',
          }));

      if (mappedCoverages.length > 0) {
        setCoveragesState(mappedCoverages);
        updateCoverages(
          mappedCoverages
            .filter(
              cov =>
                typeof cov.limitValue === 'string' &&
                cov.limitValue.trim() !== ''
            )
            .map(cov => ({
              id: cov.id,
              title: cov.title,
              amountLabel: cov.limitValue,
              franchiseLabel: cov.deductible,
            }))
        );
      }

      const assistanceCov = risk.coverages.find(
        cov => cov.productElementCode === 'ASSIST'
      );
      if (assistanceCov?.planCode) {
        setAssistanceChoice(assistanceCov.planCode);

        const resourceNameByCode = new Map(
          (planResources || []).map(resource => [
            String(resource.code || '')
              .trim()
              .toUpperCase(),
            (resource.name || '').trim(),
          ])
        );

        const assistances: string[] =
          assistanceCov.benefits
            ?.map(benefit =>
              resourceNameByCode.get(
                String(benefit.productElementCode || '')
                  .trim()
                  .toUpperCase()
              )
            )
            ?.filter((name): name is string => Boolean(name)) || [];

        updateAssistance({
          packageName: formatPackageDisplayName(
            getPlanLabel(assistanceCov.planCode)
          ),
          assistances,
          raffles: [],
        });
      }
    }

    // Prefill comercializaÃ§Ã£o (agente principal)
    if (resolvedMainAgent.agent) {
      updateCommercialization({
        brokerName: resolvedMainAgent.agent.name,
        commissionPercent: deriveCommissionPercent(resolvedMainAgent.agent),
        participationPercent: deriveParticipationPercent(
          resolvedMainAgent.agent
        ),
        coBrokers:
          (proposalDetails.agents || []).length > 3
            ? (proposalDetails.agents || [])
              .slice(3)
              .filter(agent => Boolean((agent?.name || '').trim()))
              .map(agent => ({
                name: agent?.name || '',
                participationPercent: deriveParticipationPercent(agent),
              }))
            : [],
      });
    }

    setIsPrefilled(true);
  }, [
    proposalDetails,
    mainForm,
    isPrefilled,
    propertyTypes,
    constructionTypes,
    propertyClassifications,
    insuredObjectTypes,
    coveragesCatalog,
    updateInsuranceData,
    updateProponent,
    updateCoverages,
    updateAssistance,
    updateCommercialization,
    updateProperty,
    assists,
    planResources,
    resolvedMainAgent,
  ]);

  useEffect(() => {
    if (!isPrefilled) return;

    const risk = proposalDetails.lobs?.[0]?.risks?.[0];
    const assistanceCov = risk?.coverages?.find(
      cov => cov.productElementCode === 'ASSIST'
    );
    if (!assistanceCov?.planCode) return;

    const resourceNameByCode = new Map(
      (planResources || []).map(resource => [
        String(resource.code || '')
          .trim()
          .toUpperCase(),
        (resource.name || '').trim(),
      ])
    );

    const assistances: string[] =
      assistanceCov.benefits
        ?.map(benefit =>
          resourceNameByCode.get(
            String(benefit.productElementCode || '')
              .trim()
              .toUpperCase()
          )
        )
        .filter((name): name is string => Boolean(name)) || [];

    updateAssistance({
      packageName: formatPackageDisplayName(
        getPlanLabel(assistanceCov.planCode)
      ),
      assistances,
      raffles: [],
    });
  }, [isPrefilled, proposalDetails, planResources, assists, updateAssistance]);

  useEffect(() => {
    if (!isPrefilled) return;

    const registerNumber = String(
      (mainForm.state.values as any).registerNumber || ''
    ).trim();
    if (!registerNumber) return;

    const timeoutId = window.setTimeout(() => {
      void mainForm.validateField('registerNumber' as any, 'change');
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [isPrefilled, mainForm]);

  useEffect(() => {
    if (!coveragesCatalog || coveragesState.length === 0) return;

    const byCode = new Map<string, CoverageCardData>();
    coveragesState.forEach(cov => {
      const code = String(cov.code || '').trim();
      if (!code) return;
      byCode.set(code, cov);
    });

    const merged: CoverageCardData[] = coveragesCatalog
      .map(item => {
        if (String(item.code || '').trim() === 'ASSIST') {
          return null;
        }
        const existing = byCode.get(item.code);
        const min = item.limit?.constraints?.min ?? 0;
        const max = item.limit?.constraints?.max ?? 999999999;

        const deductibleOpts = mapDeductibleOptions(item.deductibles ?? []);
        let resolvedDeductible = existing?.deductible ?? '';
        if (resolvedDeductible && deductibleOpts.length > 0) {
          const isValidValue = deductibleOpts.some(
            opt => opt.value === resolvedDeductible
          );
          if (!isValidValue) {
            const matchByCode = deductibleOpts.find(
              opt => opt.code === resolvedDeductible
            );
            if (matchByCode) {
              resolvedDeductible = matchByCode.value;
            }
          }
        }

        return {
          id: String(item.code || '').trim() || item.id,
          code: item.code,
          productElementId: item.productElementId ?? existing?.productElementId,
          title: resolveCoverageLabel(item.code),
          description: item.description || existing?.description || '',
          required: item.required,
          limit: {
            type: 'range',
            constraints: { min, max },
          },
          limitValue: existing?.limitValue ?? '',
          deductible: resolvedDeductible,
          deductibleOptions: deductibleOpts,
          indemnityPeriod:
            existing?.indemnityPeriod ?? (item.required ? '' : undefined),
        };
      })
      .filter(Boolean) as CoverageCardData[];

    const changed =
      merged.length !== coveragesState.length ||
      merged.some((cov, idx) => {
        const prev = coveragesState[idx];
        if (!prev) return true;
        return (
          cov.code !== prev.code ||
          cov.title !== prev.title ||
          cov.description !== prev.description ||
          cov.required !== prev.required ||
          cov.limitValue !== prev.limitValue ||
          cov.deductible !== prev.deductible ||
          cov.indemnityPeriod !== prev.indemnityPeriod ||
          cov.limit.constraints.min !== prev.limit.constraints.min ||
          cov.limit.constraints.max !== prev.limit.constraints.max
        );
      });

    if (!changed) return;

    setCoveragesState(merged);
    updateCoverages(
      merged
        .filter(
          cov =>
            typeof cov.limitValue === 'string' && cov.limitValue.trim() !== ''
        )
        .map(cov => ({
          id: cov.id,
          title: cov.title,
          amountLabel: cov.limitValue,
          franchiseLabel: cov.deductible,
        }))
    );
  }, [coveragesCatalog, coveragesState, updateCoverages]);

  const prefilledInsuredObjects = useMemo(() => {
    const risk = proposalDetails.lobs?.[0]?.risks?.[0];
    if (!risk) return undefined;

    const fromApi = (risk.scheduleExt ?? [])
      .map(item => ({
        insuredObjectType: String(item.insuredObject ?? '').trim(),
        riskValue:
          typeof item.value === 'number' &&
            !Number.isNaN(item.value) &&
            item.value > 0
            ? item.value.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })
            : '',
      }))
      .filter(item => item.insuredObjectType);

    if (fromApi.length > 0) return fromApi;

    return (risk.PolicyScheduleExtList ?? [])
      .map(item => ({
        insuredObjectType: String(item.ObjetoSegurado ?? '').trim(),
        riskValue:
          typeof item.RealValue === 'number' &&
            !Number.isNaN(item.RealValue) &&
            item.RealValue > 0
            ? item.RealValue.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })
            : '',
      }))
      .filter(item => item.insuredObjectType);
  }, [proposalDetails]);

  const goToStepCode = useCallback((nextCode: string) => {
    setActiveCode(nextCode);
  }, []);

  const handleNextStep = useCallback(() => {
    if (!steps.length) return;

    const safeIndex = activeIndex < 0 ? 0 : activeIndex;
    const nextIndex = Math.min(safeIndex + 1, steps.length - 1);
    const nextCode = steps[nextIndex]?.code ?? activeCode;

    if (nextCode && nextCode !== activeCode) {
      goToStepCode(nextCode);
      document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeCode, activeIndex, goToStepCode, steps]);

  const handlePreviousStep = useCallback(() => {
    if (!steps.length || activeIndex <= 0) return;

    const previousCode = steps[activeIndex - 1]?.code ?? activeCode;
    if (previousCode && previousCode !== activeCode) {
      goToStepCode(previousCode);
      document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeCode, activeIndex, goToStepCode, steps]);

  const handleResumeEditFromCalculation = useCallback(() => {
    const firstCode = steps[0]?.code ?? '';
    if (firstCode) {
      goToStepCode(firstCode);
    }
  }, [goToStepCode, steps]);

  if (isLoading) {
    return <NewQuoteFormSkeleton />;
  }

  const stepsUiIndex = activeIndex < 0 ? 0 : activeIndex;
  const isCalculationLayout = isLastStep;

  const renderActiveStep = () => {
    switch (activeCode) {
      case 'CLIENT_INFO':
        return (
          <StepInsuredData
            // @ts-ignore - Type incompatibility with composite forms
            form={mainForm}
            onNext={handleNextStep}
            onBack={handlePreviousStep}
            productId={productId || String(proposalDetails.productId)}
            productCode={productCode || proposalDetails.productCode}
            stepCode={activeCode}
            isViewMode={isViewMode}
          />
        );
      case 'RISK_BUSINESS':
        return (
          <StepRiskBusinessData
            // @ts-ignore - Type incompatibility with composite forms
            form={mainForm}
            onNext={handleNextStep}
            onBack={handlePreviousStep}
            stepCode={activeCode}
            isViewMode={isViewMode}
            initialRisks={
              (proposalDetails.lobs?.[0]?.risks ?? []) as BusinessRisk[]
            }
          />
        );
      case 'RISK_AUTO':
        return (
          <StepPropertyData
            // @ts-ignore - Type incompatibility with composite forms
            form={mainForm}
            onNext={handleNextStep}
            onBack={handlePreviousStep}
            stepCode={activeCode}
            prefilledInsuredObjects={prefilledInsuredObjects}
            onInsuredObjectsSave={setLocalInsuredObjects}
            isViewMode={isViewMode}
          />
        );
      case 'COVERAGES':
        return (
          <StepCoverages
            // @ts-ignore - Type incompatibility with composite forms
            form={mainForm}
            onNext={handleNextStep}
            onBack={handlePreviousStep}
            coverages={coveragesState}
            setCoverages={setCoveragesState}
            assistanceChoice={assistanceChoice}
            setAssistanceChoice={setAssistanceChoice}
            stepCode={activeCode}
            vrdTotal={vrdTotal > 0 ? vrdTotal : undefined}
            isViewMode={isViewMode}
          />
        );
      case 'COMMERCIALIZATION':
        return (
          <StepCommercialization
            // @ts-ignore - Type incompatibility with composite forms
            form={mainForm}
            onNext={handleNextStep}
            onBack={handlePreviousStep}
            productId={productId || String(proposalDetails.productId)}
            productCode={productCode || proposalDetails.productCode}
            prefillCommissionPercent={deriveCommissionPercent(
              resolvedMainAgent.agent
            )}
            prefillBrokerName={resolvedMainAgent.agent?.name || ''}
            prefillParticipationPercent={deriveParticipationPercent(
              resolvedMainAgent.agent
            )}
            prefillCoBrokers={resolvedCoBrokers}
            prefillBrokerAgentCode={resolvedMainAgent.agent?.agentCode || ''}
            stepCode={activeCode}
            isViewMode={isViewMode}
          />
        );
      case 'CALCULATION':
        return (
          <StepCalculation
            // @ts-ignore - Type incompatibility with composite forms
            form={mainForm}
            onBack={handlePreviousStep}
            isViewMode={isViewMode}
            onResumeEdit={
              isViewMode ? undefined : handleResumeEditFromCalculation
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className='flex flex-col gap-8 lg:flex-row lg:gap-6 xl:gap-8'>
      <div
        className={`space-y-8 min-w-0 ${isCalculationLayout ? 'flex-2' : 'flex-1'}`}
      >
        <div className='md:hidden'>
          <div className='mb-3 text-sm font-medium text-alba-gray-600'>
            Etapa {stepsUiIndex + 1} de {steps.length || 0}
          </div>
          <div className='overflow-hidden rounded-2xl bg-white px-3 py-5 shadow-md'>
            <div
              className='grid gap-1'
              style={{
                gridTemplateColumns: `repeat(${steps.length || 1}, minmax(0, 1fr))`,
              }}
            >
              {steps.map((step, index) => {
                const isActive = index === stepsUiIndex;
                const isCompleted = index < stepsUiIndex;

                return (
                  <div
                    key={step.code || step.step}
                    className='flex min-w-0 flex-col items-center gap-2'
                  >
                    <div
                      className={[
                        'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
                        isActive
                          ? 'bg-alba-blue-500 text-white'
                          : isCompleted
                            ? 'bg-alba-green-500 text-white'
                            : 'bg-alba-gray-200 text-alba-gray-600',
                      ].join(' ')}
                    >
                      {index + 1}
                    </div>
                    <span
                      className={[
                        'line-clamp-2 min-w-0 text-center text-[10px] leading-tight',
                        isActive
                          ? 'font-medium text-alba-black-500'
                          : 'text-alba-gray-600',
                      ].join(' ')}
                      title={step.stepName}
                    >
                      {step.stepName}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className='hidden md:block'>
          <Steps.Root
            step={stepsUiIndex}
            count={steps.length || 0}
            linear={!isViewMode}
          >
            <Steps.List>
              {steps.map((step, index) => (
                <Steps.Item key={step.code || step.step} index={index}>
                  <Steps.Trigger className='pointer-events-none cursor-default'>
                    <Steps.Indicator />
                    <Steps.Title>{step.stepName}</Steps.Title>
                  </Steps.Trigger>
                  {index < steps.length - 1 && <Steps.Separator />}
                </Steps.Item>
              ))}
            </Steps.List>
          </Steps.Root>
        </div>

        <div className='w-full bg-white rounded-2xl p-8 shadow-md'>
          <mainForm.Subscribe selector={state => state.values.issuanceType}>
            {issuanceType => (
              <RequiredTooltipProvider
                restartKey={`${activeCode}:${String(issuanceType ?? '')}`}
              >
                {renderActiveStep()}
              </RequiredTooltipProvider>
            )}
          </mainForm.Subscribe>
        </div>
      </div>

      {category === 'HOME_INSURANCE' && (
        <div className={isCalculationLayout ? 'lg:w-[320px] shrink-0' : ''}>
          <QuoteSummary />
        </div>
      )}
    </div>
  );
}
